<script lang="ts" setup>
import { computed } from "vue";
import { gtmTimeZones } from "~/components/Chatbot/ChatbotComposables/nodes/TgIdleNode/helper";
import { daysChips } from "~/components/Chatbot/ChatbotComposables/nodes/TgIfNode/helper";
import { stringify } from "../utils/broadcast-delay";

const props = defineProps<{
  workingDays: string[];
  workingDaysTimeInclude: string[];
  weekendDaysTimeInclude: string[];
  timezone: number;
}>();

const emit = defineEmits([
  "update:workingDays",
  "update:workingDaysTimeInclude",
  "update:weekendDaysTimeInclude",
  "update:timezone",
]);

const workingDaysArray = computed(() => {
  if (props.workingDays && props.workingDays.length) {
    return props.workingDays.join(", ");
  }
  return "";
});

const dayChips = computed(() => {
  if (props.workingDays && props.workingDays.length) {
    return daysChips
      .filter((d) => !props.workingDays.includes(d.text))
      .map((d) => d.text)
      .join(", ");
  }

  return daysChips.map((d) => d.text).join(", ");
});

const workingDays = computed({
  get(): string[] {
    return props.workingDays;
  },
  set(data: string[]) {
    emit("update:workingDays", data);
  },
});

const timezone = computed({
  get(): string {
    return `${props.timezone}`;
  },
  set(data: string) {
    const value = parseInt(data);
    if (isNaN(value)) return;
    emit("update:timezone", value);
  },
});

const workingDaysTimeBegin = computed({
  get() {
    const timeRangeString = props.workingDaysTimeInclude[0];
    if (!timeRangeString) return undefined;
    return timeRangeString.split("-")[0];
  },
  set(value: string) {
    updateWorkingDaysTimeInclude(value, workingDaysTimeEnd.value);
  },
});

const workingDaysTimeEnd = computed({
  get() {
    const timeRangeString = props.workingDaysTimeInclude[0];
    if (!timeRangeString) return undefined;
    return timeRangeString.split("-")[1];
  },
  set(value: string) {
    updateWorkingDaysTimeInclude(workingDaysTimeBegin.value, value);
  },
});

function updateWorkingDaysTimeInclude(
  dateBegin: string | undefined,
  dateEnd: string | undefined
) {
  emit("update:workingDaysTimeInclude", [stringify(dateBegin, dateEnd)]);
}
</script>

<template>
  <div class="w-full flex flex-col gap-[24px] component">
    <div class="w-full flex items-center gap-[24px]">
      <PSelect
        class="w-[172px]"
        :options="gtmTimeZones"
        size="large"
        label="Часовой пояс"
        v-model="timezone"
      />
      <PChips :chips="daysChips" v-model="workingDays" multiple />
    </div>
    <div class="w-full flex gap-[24px]">
      <div class="w-full flex flex-col gap-[12px]">
        <div class="delay-heading w-full flex gap-[12px]">
          <div class="component-heading-title">Рабочие дни:</div>
          <div class="component-heading-days">
            {{ workingDaysArray }}
          </div>
        </div>
        <div class="w-full flex gap-[12px] component-inputs items-center">
          <PInput
            size="large"
            label="Начало"
            type="time"
            v-model="workingDaysTimeBegin"
          />
          <div>-</div>
          <PInput
            size="large"
            label="Конец"
            type="time"
            v-model="workingDaysTimeEnd"
          />
        </div>
      </div>
      <div class="w-full flex flex-col gap-[12px]">
        <div class="delay-heading items-center w-full flex gap-[12px]">
          <div class="component-heading-title">Выходные:</div>
          <div class="component-heading-days">
            {{ dayChips }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss">
.component {
  &-header {
    @include semibold-22;
  }

  &-text {
    @include regular-16;

    color: var(--common-asphalt);
  }

  &-heading {
    &-title {
      @include semibold-16;
    }

    &-days {
      @include regular-16;

      color: var(--common-dark-water);
    }
  }
}
</style>
